import React, { ReactElement, useState, useEffect } from "react";
import { Modal } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { DefectItem } from "../../types/DefectItem";
import { DefectImage } from "../../types/DefectImage";
import InspectionElement from "../../types/InspectionElement";
import SelectableImageGallery from "../SelectableImageGallery";
import styles from "./AssignImagesModal.module.css";

interface Props {
    active: boolean;
    closeModal: () => void;
    defect: DefectItem;
    defectsElement?: InspectionElement;
    assignImagesToDefect?: (defect: DefectItem, selectedImages: DefectImage[]) => void;
}

export default function AssignImagesModal({
    active,
    closeModal,
    defect,
    defectsElement,
    assignImagesToDefect
}: Props): ReactElement {
    const { t } = useTranslation("common");
    const [selectedImageIds, setSelectedImageIds] = useState<string[]>([]);

    // Initialize selected images from defect when modal opens or defect changes
    useEffect(() => {
        if (defect.images) {
            try {
                const parsedImages = JSON.parse(defect.images);
                if (Array.isArray(parsedImages)) {
                    // Extract the Id field from each object
                    const imageIds = parsedImages.map((img: DefectImage) => img.Id);
                    setSelectedImageIds(imageIds);
                } else {
                    setSelectedImageIds([]);
                }
            } catch (error) {
                console.warn("Failed to parse defect images:", error);
                setSelectedImageIds([]);
            }
        } else {
            setSelectedImageIds([]);
        }
    }, [defect.images, active]);

    const handleSelectionChange = (newSelectedIds: string[]) => {
        setSelectedImageIds(newSelectedIds);
    };

    const handleAssignImages = () => {
        const defectImages: DefectImage[] = selectedImageIds.map(id => ({ Id: id }));
        assignImagesToDefect && assignImagesToDefect(defect, defectImages);
        closeModal();
    };

    return (
        <Modal
            open={active}
            onClose={closeModal}
            title={t("defects.assignImages")}
        >
            <Modal.Section>
                <div className={styles.modalContent}>
                    <div className={styles.defectInfo}>
                        <h3>{t("defects.item.title")}: {defect.shortGenName}</h3>
                        <p><strong>{t("defects.description")}:</strong> {defect.description || t("defects.noDescriptionAvailable")}</p>
                    </div>
                </div>
            </Modal.Section>
            <Modal.Section>
                <div className={styles.buttonContainer}>
                    <button 
                        className={styles.cancelButton}
                        onClick={closeModal}
                    >
                        {t("common.cancel")}
                    </button>
                    <button 
                        className={styles.assignButton}
                        onClick={handleAssignImages}
                        disabled={!defectsElement || selectedImageIds.length === 0}
                    >
                        {t("defects.assignSelectedImages")}
                    </button>
                </div>
            </Modal.Section>
            <Modal.Section>
                <div className={styles.modalContent}>
                    {defectsElement ? (
                        <SelectableImageGallery
                            data={defectsElement}
                            selectedImageIds={selectedImageIds}
                            onSelectionChange={handleSelectionChange}
                        />
                    ) : (
                        <div className={styles.noImages}>
                            <p>{t("defects.noImagesAvailable")}</p>
                        </div>
                    )}
                </div>
            </Modal.Section>
        </Modal>
    );
}
